import { createStore } from 'fe-lib-store';
import {
  getAppIdsForCurrentView,
  selectAppsSearchResults,
  hasSearchResults,
  selectAllFeaturedAppIds,
  selectFeaturedAppIdsByCategoryId,
  selectPaginatedAppIdsByPageNumber,
  selectRelatedAppsByAppId,
  selectRelatedAppsByCategoryId,
  selectCategoryBySlug,
  selectFeaturedCategoryAppsByCategorySlug,
  selectCategoryAppsByCategorySlug,
  getNextPage,
  getAppsView,
  getAppUrl,
  getCategoryUrl,
} from './selectors';
import {
  getApps as apiGetApps,
  getAppById as apiGetAppById,
  getRelatedAppsByAppId as apiGetRelatedAppsByAppId,
  getCategories as apiGetCategories,
  getCategoryTypes as apiGetCategoryTypes,
} from 'fe-appdir-lib-api';
import { getSingleton, registerSingleton } from 'fe-lib-singleton';

const NAMESPACE = 'fe-appdir-data-app';

// Grab the server-side rendered state
const INITIAL_STATE =
  typeof window !== 'undefined'
    ? window.__INITIAL_STATE__ // eslint-disable-line no-underscore-dangle
    : false;

// Use the server-side rendered state on client,
// or create a new state object on the server
const initialState = INITIAL_STATE || {
  apps: {
    byId: {},
    idsBySlug: {},
    idsByCategoryId: {},
    featuredAppIdsByCategoryId: {},
    allIds: [],
    idsByPage: [],
    allFeaturedAppIds: [],
    searchResultsIds: [],
  },
  categories: {
    byId: {},
    idsBySlug: {},
    allIds: [],
  },
  categoryTypes: {
    byId: {},
    allIds: [],
  },
  paginationPage: 1,
  haveLoadedAllPages: false,
  isFetchingPaginatedApps: false,
  isFetchingAppsSearchResults: false,
  isUserSearching: false,
  isLoggedIn: false,
  searchQuery: '',
  // appsSearchResultIds: [],
  // categoriesSearchResultIds: [],
  // categoryIds: [],
  // categories: {},
  // categoryTypeIds: [],
  // categoryTypes: {},
};

// Define ReactSnap callback for pre-rendering
typeof window !== 'undefined'
  ? (window.snapSaveState = () => ({
      __INITIAL_STATE__: store.getState(),
    }))
  : null;

const handlers = {
  toggleLoggedIn: (state) => ({
    ...state,
    isLoggedIn: !state.isLoggedIn,
  }),

  toggleIsFetchingPaginatedApps: (state) => ({
    ...state,
    isFetchingPaginatedApps: !state.isFetchingPaginatedApps,
  }),

  toggleIsFetchingAppsSearchResults: (state, value) => ({
    ...state,
    isFetchingAppsSearchResults:
      value === undefined ? !state.isFetchingAppsSearchResults : Boolean(value),
  }),

  toggleIsUserSearching: (state, value) => ({
    ...state,
    isUserSearching:
      value === undefined ? !state.isUserSearching : Boolean(value),
  }),

  updateSearchQuery: (state, query) => ({
    ...state,
    searchQuery: query,
  }),

  setAppsView: (state, view) => ({
    ...state,
    apps: {
      ...state.apps,
      view,
    },
  }),

  getApps: (state, apps) => ({
    ...state,
    apps: {
      ...state.apps,
      byId: {
        ...state.apps.byId,
        ...apps.reduce((newByIdState, app) => {
          const newApp = Object.prototype.hasOwnProperty.call(
            state.apps.byId,
            app.id,
          )
            ? {
                ...state.apps.byId[app.id],
                ...app,
              }
            : app;

          return {
            ...newByIdState,
            [app.id]: newApp,
          };
        }, state.apps.byId),
      },
      idsBySlug: {
        ...state.apps.idsBySlug,
        ...apps.reduce(
          (newIdsBySlugState, app) => ({
            ...newIdsBySlugState,
            [app.slug]: app.id,
          }),
          state.apps.idsBySlug,
        ),
      },
      idsByCategoryId: {
        ...state.apps.idsByCategoryId,
        ...apps.reduce((newIdsByCategoryIdState, app) => {
          const categoryId = Object.prototype.hasOwnProperty.call(
            app,
            'primaryCategory',
          )
            ? app.primaryCategory.id
            : null;
          const idsInCategory =
            categoryId &&
            Object.prototype.hasOwnProperty.call(
              newIdsByCategoryIdState,
              categoryId,
            )
              ? newIdsByCategoryIdState[categoryId]
              : null;

          return categoryId && idsInCategory
            ? {
                ...newIdsByCategoryIdState,
                [categoryId]: [
                  app.id,
                  ...((idsInCategory &&
                    idsInCategory.filter((id) => id !== app.id)) ||
                    []),
                ],
              }
            : newIdsByCategoryIdState;
        }, state.apps.idsByCategoryId),
      },
      allIds: apps.reduce(
        (newAllIdsState, app) =>
          !newAllIdsState.includes(app.id)
            ? [...newAllIdsState, app.id]
            : newAllIdsState,
        state.apps.allIds,
      ),
    },
  }),

  getCategoryApps: (state, apps, categoryId) => ({
    ...state,
    apps: {
      ...state.apps,
      idsByCategoryId: {
        [categoryId]: [
          ...(state.apps.idsByCategoryId[categoryId] || []),
          ...apps
            .filter((app) =>
              state.apps.idsByCategoryId[categoryId]
                ? !state.apps.idsByCategoryId[categoryId].includes(app.id)
                : true,
            )
            .map((app) => app.id),
        ],
      },
    },
  }),

  getPaginatedAppIds: (state, paginatedApps) => ({
    ...state,
    apps: {
      ...state.apps,
      idsByPage: [
        ...state.apps.idsByPage,
        paginatedApps.map((paginatedApp) => paginatedApp.id),
      ],
    },
  }),

  resetAppsIdsByPage: (state) => ({
    ...state,
    apps: {
      ...state.apps,
      idsByPage: [],
    },
  }),

  getFeaturedAppIds: (state, featuredApps) => ({
    ...state,
    apps: {
      ...state.apps,
      allFeaturedAppIds: [
        ...state.apps.allFeaturedAppIds,
        ...featuredApps
          .filter((app) => !state.apps.allFeaturedAppIds.includes(app.id))
          .map((app) => app.id),
      ],
    },
  }),

  getFeaturedAppIdsByCategoryId: (state, categoryId, featuredApps) => ({
    ...state,
    apps: {
      ...state.apps,
      featuredAppIdsByCategoryId: {
        ...state.apps.featuredAppIdsByCategoryId,
        [categoryId]: [
          ...(state.apps.featuredAppIdsByCategoryId[categoryId] || []),
          ...featuredApps
            .filter((app) =>
              state.apps.featuredAppIdsByCategoryId[categoryId]
                ? !state.apps.featuredAppIdsByCategoryId[categoryId].includes(
                    app.id,
                  )
                : true,
            )
            .map((app) => app.id),
        ],
      },
    },
  }),

  // Adds all app ids from a category into the apps.idsByCategory[categoryId] state
  getAppIdsByCategoryId: (state, categoryId, appsInCategory) => ({
    ...state,
    apps: {
      ...state.apps,
      idsByCategoryId: {
        ...state.apps.idsByCategoryId,
        [categoryId]: [
          ...(state.apps.idsByCategoryId[categoryId] || []),
          ...appsInCategory
            .filter((app) =>
              state.apps.idsByCategoryId[categoryId]
                ? !state.apps.idsByCategoryId[categoryId].includes(app.id)
                : true,
            )
            .map((app) => app.id),
        ],
      },
    },
  }),

  getAppById: (state, app) => ({
    ...state,
    apps: {
      ...state.apps,
      byId: {
        ...state.apps.byId,
        [app.id]: app,
      },
      idsBySlug: {
        ...state.apps.idsBySlug,
        [app.slug]: app.id,
      },
      idsByCategoryId: {
        ...state.apps.idsByCategoryId,
        ...app.categories.reduce(
          (idsByCategoryIdState, category) => ({
            ...idsByCategoryIdState,
            [category.id]:
              Object.prototype.hasOwnProperty.call(
                idsByCategoryIdState,
                category.id,
              ) && idsByCategoryIdState[category.id].includes(app.id)
                ? idsByCategoryIdState[category.id]
                : [...(idsByCategoryIdState[category.id] || []), app.id],
          }),
          state.apps.idsByCategoryId,
        ),
      },
      allIds: !state.apps.allIds.includes(app.id)
        ? [...state.apps.allIds, app.id]
        : state.apps.allIds,
    },
  }),

  getRelatedAppIdsByAppId: (state, appId, relatedApps) => ({
    ...state,
    apps: {
      ...state.apps,
      byId: {
        ...state.apps.byId,
        [appId]: {
          ...state.apps.byId.appId,
          relatedAppIds: relatedApps.map((relatedApp) => relatedApp.id),
        },
      },
    },
  }),

  getCategories: (state, categories) => ({
    ...state,
    categories: {
      ...state.categories,
      byId: {
        ...state.categories.byId,
        ...categories.reduce(
          (categoriesState, category) => ({
            ...categoriesState,
            [category.id]: category,
          }),
          state.categories.byId,
        ),
      },
      idsBySlug: {
        ...state.categories.idsBySlug,
        ...categories.reduce(
          (idsBySlugState, category) => ({
            ...idsBySlugState,
            [category.slug]: category.id,
          }),
          state.categories.idsBySlug,
        ),
      },
      allIds: categories.reduce(
        (ids, category) =>
          !ids.includes(category.id) ? ids.concat(category.id) : ids,
        state.categories.allIds,
      ),
    },
  }),

  getCategoryTypes: (state, categoryTypes) => ({
    ...state,
    categoryTypes: {
      ...state.categoryTypes,
      byId: {
        ...state.categoryTypes.byId,
        ...categoryTypes.reduce(
          (categoryTypesState, categoryType) => ({
            ...categoryTypesState,
            [categoryType.id]: categoryType,
          }),
          state.categoryTypes.byId,
        ),
      },
      allIds: categoryTypes.reduce(
        (ids, categoryType) =>
          !ids.includes(categoryType.id) ? ids.concat(categoryType.id) : ids,
        state.categoryTypes.allIds,
      ),
    },
  }),

  getAppsSearchResults: (state, apps) => ({
    ...state,
    apps: {
      ...state.apps,
      searchResultsIds: apps.map((app) => app.id),
    },
  }),

  incrementPaginationPage: (state) => ({
    ...state,
    paginationPage: state.paginationPage + 1,
  }),

  haveLoadedAllPages: (state) => ({
    ...state,
    haveLoadedAllPages: true,
  }),

  resetPaginationPage: (state) => ({
    ...state,
    paginationPage: 1,
    haveLoadedAllPages: false,
  }),

  resetState: () => initialState,
};

let singletonStore = getSingleton(NAMESPACE);

if (!singletonStore) {
  singletonStore = createStore(NAMESPACE, handlers, initialState);

  registerSingleton(NAMESPACE, singletonStore);
}

const { store, dispatch, getState, subscribe } = singletonStore;
const toggleLoggedIn = async () => dispatch('toggleLoggedIn');

const updateSearchQuery = (query) => {
  dispatch('updateSearchQuery', query);
  toggleIsUserSearching(true);
};

// If we're changing view, we need to clean the pages of apps previously loaded
const setAppsView = (view) => {
  if (getAppsView(getState()) !== view) dispatch('resetAppIdsByPage');
  dispatch('setAppsView', view);
};

/**
 * Attempts to get the information for all apps and merge it into state
 * @param {}
 * @returns {Object|bool} - The dispatch object response if the call was successful, false otherwise
 */
const getApps = async (queryArguments = {}) => {
  try {
    const apps = await apiGetApps(queryArguments);

    return dispatch('getApps', apps);
  } catch (error) {
    console.error(error); // eslint-disable-line no-console

    return false;
  }
};

const fetchPaginatedApps = async (queryArguments = {}) => {
  try {
    dispatch('toggleIsFetchingPaginatedApps');

    const moreApps = await apiGetApps({
      pageNumber: getNextPage(getState()),
      pageSize: 16,
      ...queryArguments,
    });

    dispatch('getApps', moreApps);
    dispatch('getMoreAppIds', moreApps);
  } catch (error) {
    console.error(error); // eslint-disable-line no-console
  } finally {
    dispatch('toggleIsFetchingPaginatedApps');
  }
};

const getPaginatedApps = async (pageNumber, queryArguments = {}) => {
  const pageSize = 16;

  try {
    dispatch('toggleIsFetchingPaginatedApps');

    const paginatedApps = await apiGetApps({
      ...queryArguments,
      pageNumber,
      pageSize: pageSize,
      sortBy: 'name',
      sortDirection: 'asc',
    });

    if (paginatedApps.length < pageSize) {
      dispatch('haveLoadedAllPages');
    }

    dispatch('incrementPaginationPage');
    dispatch('getApps', paginatedApps);
    dispatch('getPaginatedAppIds', paginatedApps);
  } catch (error) {
    console.error(error); // eslint-disable-line no-console

    return false;
  } finally {
    dispatch('toggleIsFetchingPaginatedApps');
  }
};

const getPaginatedCategoryApps = async (
  pageNumber,
  categoryId,
  queryArguments = {},
) => {
  const pageSize = 16;

  try {
    dispatch('toggleIsFetchingPaginatedApps');

    const paginatedApps = await apiGetApps({
      ...queryArguments,
      categoryIds: categoryId,
      pageNumber: pageNumber,
      pageSize: pageSize,
      sortBy: 'name',
      sortDirection: 'asc',
    });

    if (paginatedApps.length < pageSize) {
      dispatch('haveLoadedAllPages');
    }

    dispatch('incrementPaginationPage');
    dispatch('getApps', paginatedApps);
    dispatch('getCategoryApps', paginatedApps, categoryId);
    dispatch('getPaginatedAppIds', paginatedApps);
  } catch (error) {
    console.error(error); // eslint-disable-line no-console

    return false;
  } finally {
    dispatch('toggleIsFetchingPaginatedApps');
  }
};

const getMoreAppsByCategoryId = async (categoryId) =>
  await fetchPaginatedApps({ categoryIds: [categoryId] });

const getFeaturedApps = async (queryArguments = {}) => {
  try {
    const featuredApps = await apiGetApps({
      ...queryArguments,
      featureStatus: 1,
    });

    dispatch('getApps', featuredApps);

    return dispatch('getFeaturedAppIds', featuredApps);
  } catch (error) {
    console.error(error); // eslint-disable-line no-console

    return error;
  }
};

const getFeaturedAppsByCategoryId = async (categoryId) => {
  try {
    const featuredApps = await apiGetApps({
      featureStatus: 1,
      categoryIds: [categoryId],
    });

    dispatch('getApps', featuredApps);

    return dispatch('getFeaturedAppIdsByCategoryId', categoryId, featuredApps);
  } catch (error) {
    console.error(error); // eslint-disable-line no-console

    return error;
  }
};

/**
 * Attempts to get the information for a given app and merge it into state
 * @param {string|number} id - The ID of the app we want the information for
 * @returns {Object|bool} - The dispatch object response if the call was successful, false otherwise
 */
const getAppById = async (id) => {
  try {
    const app = await apiGetAppById(id);
    return dispatch('getAppById', app);
  } catch (error) {
    return false;
  }
};

/**
 * Attempts to fetch related apps by app id
 *
 * @param {String|Number} appId - the id of the app in relation
 * @returns {Object|Boolean}
 */
const getRelatedAppsByAppId = async (appId) => {
  try {
    const relatedApps = await apiGetRelatedAppsByAppId(appId);

    dispatch('getApps', relatedApps);

    return dispatch('getRelatedAppIdsByAppId', appId, relatedApps);
  } catch (error) {
    console.error(error); // eslint-disable-line no-console

    return error;
  }
};

/**
 * Attempts to get all the categories and merge them into state
 * @param {}
 * @returns {Object|bool} - The dispatch object response if the call was successful, false otherwise
 */
const getCategories = async (queryArguments = {}) => {
  try {
    const categories = await apiGetCategories(queryArguments);
    return dispatch('getCategories', categories);
  } catch (error) {
    return false;
  }
};

const getCategoryBySlug = async (slug) => {
  try {
    const category = await apiGetCategories({ slug: slug });

    dispatch('getCategories', category);
  } catch (error) {
    console.error(error); // eslint-disable-line no-console
  }
};

const getCategoryTypes = async () => {
  try {
    const categoryTypes = await apiGetCategoryTypes();
    return dispatch('getCategoryTypes', categoryTypes);
  } catch (error) {
    return false;
  }
};

const getAppsByCategoryId = async (categoryId) => {
  try {
    const apps = await apiGetApps({ categoryIds: [categoryId] });
    dispatch('getApps', apps);
    return dispatch('getAppIdsByCategoryId', categoryId, apps);
  } catch (error) {
    return false;
  }
};

/**
 * Attempts to get all the apps search results and merge them into state
 * @returns {undefined|bool} - Undefined if the calls were successful, false otherwise
 */
const getSearchResults = async (query) => {
  try {
    // No need to search if the user has deleted all characters
    // @TODO: consider extending that condition to query.length<3
    if (!query) {
      clearSearchResults();
      return;
    }

    toggleIsFetchingSearchResults(true);

    const searchResultsApps = await apiGetApps({
      searchTerm: query,
    });

    dispatch('getApps', searchResultsApps);
    dispatch('getAppsSearchResults', searchResultsApps);
  } catch (error) {
    console.error(error); // eslint-disable-line no-console
  } finally {
    toggleIsFetchingSearchResults(false);
  }
};

const clearSearchResults = () => {
  dispatch('getAppsSearchResults', []);
};

/**
 * Gets all categories belonging to category type of the provided ID currently in state
 * (This is technically a selector, but we need it here to access the state because
 * it is used in a fulfill() call that doesn't have access to the state)
 * @param {id} string|number - the ID of the category type we are looking for
 * @returns {[]string} - An array of category ids in that category type
 */
const getCategoryIdsByCategoryTypeId = (id) => {
  const idString = id.toString(); // coerce to string as that is how the IDs are returned from endpoint
  const state = store.getState();
  return Object.keys(state.categories.byId).filter(
    (categoryId) =>
      state.categories.byId[categoryId].categoryTypeId === idString,
  );
};

const toggleIsFetchingPaginatedApps = () => {
  return dispatch('toggleIsFetchingPaginatedApps');
};

const toggleIsFetchingSearchResults = (value) => {
  toggleIsFetchingAppsSearchResults(value);
};

const toggleIsFetchingAppsSearchResults = (value) => {
  return dispatch('toggleIsFetchingAppsSearchResults', value);
};

const toggleIsUserSearching = (value) => {
  return dispatch('toggleIsUserSearching', value);
};

const resetPagination = () => {
  dispatch('resetAppsIdsByPage');
  dispatch('resetPaginationPage');
};

const resetState = () => {
  dispatch('resetState');
};

// Store Utils
export { store, getState, subscribe };

// Reducers
export { handlers };

// Action Creators
export {
  toggleLoggedIn,
  updateSearchQuery,
  setAppsView,
  getApps,
  getPaginatedApps,
  getPaginatedCategoryApps,
  getMoreAppsByCategoryId,
  getFeaturedApps,
  getFeaturedAppsByCategoryId,
  getAppById,
  getRelatedAppsByAppId,
  getCategories,
  getCategoryBySlug,
  getCategoryTypes,
  getAppsByCategoryId,
  getSearchResults,
  clearSearchResults,
  toggleIsFetchingPaginatedApps,
  toggleIsFetchingSearchResults,
  toggleIsFetchingAppsSearchResults,
  toggleIsUserSearching,
  resetPagination,
  resetState,
};

// Selectors
export {
  // Selectors
  selectAllFeaturedAppIds,
  selectFeaturedAppIdsByCategoryId,
  selectPaginatedAppIdsByPageNumber,
  getAppIdsForCurrentView,
  selectAppsSearchResults,
  selectRelatedAppsByAppId,
  selectRelatedAppsByCategoryId,
  hasSearchResults,
  getNextPage,
  getAppsView,
  getAppUrl,
  getCategoryUrl,
  getCategoryIdsByCategoryTypeId,
  selectCategoryBySlug,
  selectFeaturedCategoryAppsByCategorySlug,
  selectCategoryAppsByCategorySlug,
};
